import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "react-toastify";
import { CreditCard } from "lucide-react";
import { format } from "date-fns";

interface AssignedRental {
  periodNo: number | string;
  fromDate: string;
  toDate: string;
  dueDate: string;
  baseRent?: number;
  gst?: number;
  tds?: number;
  total?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noaId: string;
  lenderName: string;
  fundingMode: "A" | "B" | "C"; // A = Pre-GST, B = Post-GST, C = Post-TDS
  assignedRentals: AssignedRental[];
  currentNachStatus: boolean;
  currentNachStartPeriod?: number | string;
  onSave: (nachActive: boolean, nachStartPeriod: number | string, nachType: "full" | "base_only", dueDate: string) => void;
}

export default function NoaNachSetupModal({ 
  open, 
  onOpenChange, 
  noaId,
  lenderName,
  fundingMode,
  assignedRentals, 
  currentNachStatus,
  currentNachStartPeriod,
  onSave
}: Props) {
  const [nachActive, setNachActive] = useState(currentNachStatus);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    currentNachStartPeriod?.toString() || ""
  );

  // NACH type is auto-derived from funding mode
  // Pre-GST (A) = base_only, Post-GST (B) / Post-TDS (C) = full
  const nachType: "full" | "base_only" = fundingMode === "A" ? "base_only" : "full";

  const getFundingModeLabel = () => {
    switch (fundingMode) {
      case "A": return "Pre-GST";
      case "B": return "Post-GST";
      case "C": return "Post-TDS";
      default: return fundingMode;
    }
  };

  const getNachAmountDescription = () => {
    if (fundingMode === "A") {
      return "Base Rental only (GST net of TDS paid separately to Astrafin)";
    }
    return "Total Amount (Base + GST - TDS)";
  };

  const handleSave = () => {
    if (nachActive && !selectedPeriod) {
      toast.error("Please select the starting period for NACH");
      return;
    }

    const periodValue = selectedPeriod === "interim" ? "i" : parseInt(selectedPeriod) || 1;
    
    // Find the dueDate for the selected period
    const selectedRental = assignedRentals.find((rental) => {
      const rentalPeriod = rental.periodNo === "i" ? "interim" : String(rental.periodNo);
      return rentalPeriod === selectedPeriod;
    });
    
    const dueDate = selectedRental?.dueDate || "";
    
    if (nachActive && !dueDate) {
      toast.error("Unable to find due date for selected period");
      return;
    }
    
    onSave(nachActive, periodValue, nachType, dueDate);
    
    if (nachActive) {
      toast.success(`NACH activated from Period ${selectedPeriod === "interim" ? "Interim" : selectedPeriod}`);
    } else {
      toast.success("NACH deactivated");
    }

    onOpenChange(false);
  };

  const getPeriodLabel = (periodNo: number | string) => {
    if (periodNo === "i" || periodNo === "interim") return "Interim";
    return `Period ${periodNo}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            NACH Setup - {noaId}
          </DialogTitle>
          <DialogDescription>
            Configure NACH (National Automated Clearing House) for lender-assigned rentals. 
            Only rentals assigned to <span className="font-medium">{lenderName}</span> are shown below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* NACH Status Toggle */}
          {/* <div className="space-y-3">
            <Label className="text-sm font-medium">NACH Status</Label>
            <RadioGroup 
              value={nachActive ? "active" : "inactive"} 
              onValueChange={(v) => setNachActive(v === "active")}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="inactive" id="inactive" />
                <Label htmlFor="inactive" className="flex items-center gap-2 cursor-pointer">
                  <Badge variant="outline" className="bg-muted">Inactive</Badge>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="active" id="active" />
                <Label htmlFor="active" className="flex items-center gap-2 cursor-pointer">
                  <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                </Label>
              </div>
            </RadioGroup>
          </div> */}

         
              {/* Starting Period */}
              <div className="space-y-2">
                <Label htmlFor="start-period">Start NACH From</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger id="start-period">
                    <SelectValue placeholder="Select starting period..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assignedRentals.map((rental) => (
                      <SelectItem 
                        key={String(rental.periodNo)} 
                        value={rental.periodNo === "i" ? "interim" : String(rental.periodNo)}
                      >
                        {getPeriodLabel(rental.periodNo)} - Due: {format(new Date(rental.dueDate), "dd MMM yyyy")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  NACH auto-debit will apply from this period onwards
                </p>
              </div>

              {/* NACH Collection Info - Auto-derived */}
              <div className="space-y-2 p-3 bg-muted/50 rounded-md border">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">NACH Collection Amount</Label>
                  <Badge variant="outline">{getFundingModeLabel()} Funding</Badge>
                </div>
                <p className="text-sm text-foreground font-medium">
                  {getNachAmountDescription()}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fundingMode === "A" 
                    ? "Since this is Pre-GST funding, lender will auto-debit only the base rental amount."
                    : "Since this is Post-GST/Post-TDS funding, lender will auto-debit the full amount (Base + GST - TDS)."}
                </p>
              </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {nachActive ? "Activate NACH" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
